import { useEffect, useMemo } from "react";
import { NetPresentValue } from "./npv-utils";
import { useAppSelector } from "@hooks/useAppSelector";
import { SetNpv } from "@redux/npv/npv-actions";
import { DataGrid } from "devextreme-react";
import { useAppDispatch } from "@hooks/useAppDispatch";
import { Column, Editing, Paging, Scrolling } from "devextreme-react/data-grid";
import DataSource from "devextreme/data/data_source";
import { calculateAge } from "../UP/up-utils";
import { formatNumber } from "devextreme/localization";
import npvSlice, { npvActions } from "@redux/npv/npv-slice";
import { uqActions } from "@redux/uq/uq-slice";
import type { NPVRow, NPVSettings, UniquePossibilities, Clients, UPRow } from "@/types/types";
import { ColumnCellTemplateData } from "devextreme/ui/data_grid";
import { CalculateGrid } from "@/redux/uq/uq-actions";
import AgeCell from "../UPandNPVShared/AgeCell";
import GridHeader from "../UPandNPVShared/GridHeader";
import GridCell from "../UPandNPVShared/GridCell";

type Props = {
  uniquePossibilities: UniquePossibilities;
  household: Required<Clients>;
  showHiddenRows: boolean;
};

export default function Grid({ uniquePossibilities, household, showHiddenRows }: Props) {
  const dispatch = useAppDispatch();
  const { gridSet, settings } = useAppSelector((state) => state.npv);
  const { indexShift } = useAppSelector((state) => state.uq);
  const { age1, age2 } = useAppSelector((state) => state.household);

  useEffect(() => {
    if (uniquePossibilities.data) {
      dispatch(npvActions.setIsModified(false));
      const { gridSet: uqGridset, settings, titles } = JSON.parse(uniquePossibilities.data);
      dispatch(CalculateGrid(0, uqGridset, settings)).then((updatedGrid) => {
        dispatch(uqActions.setIsModified(false));

        const newSettings: NPVSettings = {
          person1: {
            ...npvSlice.getInitialState().settings.person1,
            ...settings.person1,
          },
          person2: {
            ...npvSlice.getInitialState().settings.person2,
            ...settings.person2,
          },
          irr: npvSlice.getInitialState().settings.irr,
          npvAge: npvSlice.getInitialState().settings.npvAge,
          yearsToRetire: npvSlice.getInitialState().settings.yearsToRetire,
        };

        const npvSettingsServer = JSON.parse(uniquePossibilities?.npvSettings as string);

        const maxAgePrimary = age1 >= 90 ? 110 : 100;
        const maxAgeSecondary = age2 ? (age2 >= 90 ? 110 : 100) : maxAgePrimary;

        const maxAge = Math.max(maxAgePrimary, maxAgeSecondary);
        dispatch(npvActions.setMaxClientsAge(maxAge));
        dispatch(npvActions.setShouldStopAtAge(Math.min(maxAgePrimary, maxAgeSecondary)));

        if (npvSettingsServer) {
          const settingsParsed = JSON.parse(uniquePossibilities.npvSettings as string);
          const settingsToUse = {
            ...newSettings,
            ...settingsParsed,
          };

          dispatch(npvActions.setSettings(settingsToUse));

          const { newHelpers, npv } = NetPresentValue.calcHelpers(
            settingsToUse,
            updatedGrid,
            age1,
            age2
          );
          const npvGrid = NetPresentValue.createNpv(
            updatedGrid,
            newHelpers,
            npv,
            settingsToUse,
            household,
            updatedGrid
          );

          dispatch(SetNpv(settingsToUse, household, titles, npvGrid, newHelpers, npv));
        } else {
          const { newHelpers, npv } = NetPresentValue.calcHelpers(
            newSettings,
            updatedGrid,
            age1,
            age2
          );
          const npvGrid = NetPresentValue.createNpv(
            updatedGrid,
            newHelpers,
            npv,
            newSettings,
            household,
            updatedGrid
          );

          dispatch(SetNpv(newSettings, household, titles, npvGrid, newHelpers, npv));
        }
      });
    }
  }, [
    age1,
    age2,
    dispatch,
    household,
    indexShift,
    uniquePossibilities.data,
    uniquePossibilities.npvSettings,
  ]);

  const dataSource = useMemo(() => {
    const filteredData = gridSet?.filter((i: UPRow) => i.Age.value >= age1 || !i.Age.value);
    const indexShift = gridSet?.length - filteredData?.length;

    dispatch(uqActions.setIndexShift(showHiddenRows ? 0 : indexShift));

    return {
      data: showHiddenRows ? gridSet : filteredData,
      indexShift: showHiddenRows ? 0 : indexShift,
    };
  }, [gridSet, showHiddenRows]);

  return gridSet ? (
    <DataGrid
      showBorders={true}
      showRowLines={true}
      showColumnLines={true}
      remoteOperations={false}
      wordWrapEnabled={true}
      className="ao-grid grid-sticky-headers print:mt-4"
      onCellPrepared={(e) => {
        if (e.rowType === "header") {
          e.cellElement.style.backgroundColor = "var(--primary-color)";
          e.cellElement.style.color = "white";
        } else if (e.column.name == "Age" || e.column.name == "Age2") {
          e.cellElement.classList.add("bg-primary");
          e.cellElement.style.color = "white";
        }
      }}
      dataSource={dataSource.data as unknown as DataSource<NPVRow[]>}
    >
      <Column dataField={"id"} visible={false} />
      <Column
        dataField={"Age"}
        allowSorting={false}
        allowEditing={false}
        alignment={"center"}
        width={"8%"}
        visible={settings?.person1?.showAge}
        cellRender={(data: ColumnCellTemplateData<NPVRow>) => {
          return (
            <AgeCell
              data={data as ColumnCellTemplateData<NPVRow | UPRow>}
              clientExternalCode={household.primaryContact.externalCode}
              isNpv
            />
          );
        }}
        headerCellRender={() => (
          <GridHeader headerName={`${household?.primaryContact?.preferredFirstName}`} isNpv />
        )}
      />
      <Column
        dataField={"Age2"}
        allowSorting={false}
        allowEditing={false}
        visible={!!household?.secondaryContact?.birthDate && settings.person2?.showAge}
        alignment={"center"}
        width={"8%"}
        cellRender={(data: ColumnCellTemplateData<NPVRow>) => {
          return (
            <AgeCell
              data={data as ColumnCellTemplateData<NPVRow | UPRow>}
              clientExternalCode={household.secondaryContact.externalCode}
              isSecondary
              isNpv
            />
          );
        }}
        headerCellRender={() => (
          <GridHeader headerName={household?.secondaryContact?.preferredFirstName ?? ""} isNpv />
        )}
      />
      <Column
        dataField={"NPVSolution"}
        allowSorting={false}
        allowEditing={false}
        alignment={"center"}
        width={"fit"}
        cellRender={(data: ColumnCellTemplateData<NPVRow>) => (
          <GridCell data={data as ColumnCellTemplateData<NPVRow | UPRow>} isNpv />
        )}
        headerCellRender={() => <GridHeader headerName="NPV Solution" header="NPVSolution" isNpv />}
      />

      <Column
        dataField={"SocialSecurity"}
        allowSorting={false}
        allowEditing={false}
        width={"fit"}
        alignment={"center"}
        cellRender={(data: ColumnCellTemplateData<NPVRow>) => (
          <GridCell data={data as ColumnCellTemplateData<NPVRow | UPRow>} isNpv />
        )}
        headerCellRender={() => (
          <GridHeader isNpv headerName="Social Security" header="SocialSecurity" />
        )}
      />

      <Column
        dataField={"Pension"}
        allowSorting={false}
        allowEditing={false}
        width={"fit"}
        alignment={"center"}
        cellRender={(data: ColumnCellTemplateData<NPVRow>) => (
          <GridCell data={data as ColumnCellTemplateData<NPVRow | UPRow>} isNpv />
        )}
        headerCellRender={() => <GridHeader isNpv headerName="Pension" header="Pension" />}
      />

      <Column
        dataField={"Income"}
        allowSorting={false}
        allowEditing={false}
        width={"fit"}
        alignment={"center"}
        cellRender={(data: ColumnCellTemplateData<NPVRow>) => (
          <GridCell data={data as ColumnCellTemplateData<NPVRow | UPRow>} isNpv hasContextMenu />
        )}
        headerCellRender={() => <GridHeader headerName="Income 1" header="Income1" isNpv />}
      />

      <Column
        dataField={"Income2"}
        allowSorting={false}
        allowEditing={false}
        width={"fit"}
        alignment={"center"}
        cellRender={(data: ColumnCellTemplateData<NPVRow>) => (
          <GridCell data={data as ColumnCellTemplateData<NPVRow | UPRow>} isNpv hasContextMenu />
        )}
        headerCellRender={() => <GridHeader isNpv headerName="Income 2" header="Income2" />}
      />

      <Column
        dataField={"Spending"}
        allowSorting={false}
        allowEditing={false}
        width={"fit"}
        alignment={"center"}
        cellRender={(data: ColumnCellTemplateData<NPVRow>) => (
          <GridCell data={data as ColumnCellTemplateData<NPVRow | UPRow>} isNpv hasContextMenu />
        )}
        headerCellRender={() => <GridHeader headerName="Spending 1" header="Spending" isNpv />}
      />

      <Column
        dataField={"Spending2"}
        allowSorting={false}
        allowEditing={false}
        width={"fit"}
        alignment={"center"}
        cellRender={(data: ColumnCellTemplateData<NPVRow>) => (
          <GridCell data={data as ColumnCellTemplateData<NPVRow | UPRow>} isNpv hasContextMenu />
        )}
        headerCellRender={() => <GridHeader headerName="Spending 2" header="Spending2" isNpv />}
      />

      <Column
        dataField={"Spending3"}
        allowSorting={false}
        allowEditing={false}
        width={"fit"}
        alignment={"center"}
        cellRender={(data: ColumnCellTemplateData<NPVRow>) => (
          <GridCell data={data as ColumnCellTemplateData<NPVRow | UPRow>} isNpv hasContextMenu />
        )}
        headerCellRender={() => <GridHeader headerName="Spending 3" header="Spending3" isNpv />}
      />

      <Column
        dataField={"YearEnd"}
        allowSorting={false}
        allowEditing={false}
        alignment={"center"}
        width={"fit"}
        headerCellRender={() => <GridHeader headerName="Year End" header="YearEnd" isNpv />}
        cellRender={(data: ColumnCellTemplateData<NPVRow, string>) => {
          const age1 = calculateAge(household.primaryContact.birthDate.toString(), 0).value;

          return (
            <div
              className={`relative bg-secondary px-2 py-[14px] text-sm print:px-1 print:py-1 print:!text-[10px] ${
                data.value < 0 ? "text-danger" : ""
              } ${data.row.data.Age.value && age1 > data.row.data.Age.value ? "italic" : ""} `}
            >
              {formatNumber(data.value, "currency").replaceAll("(", "").replaceAll(")", "")}
            </div>
          );
        }}
      />

      <Column
        dataField={"InvestmentWithdrawals"}
        allowSorting={false}
        allowEditing={false}
        alignment={"center"}
        width={"fit"}
        cellRender={(data: ColumnCellTemplateData<NPVRow>) => (
          <GridCell data={data as ColumnCellTemplateData<NPVRow | UPRow>} isNpv />
        )}
        headerCellRender={() => (
          <GridHeader headerName="Investment Withdrawals" header="InvestmentWithdrawals" isNpv />
        )}
      />

      <Editing mode="cell" allowUpdating={true} />
      <Scrolling showScrollbar="always" scrollByThumb={true} scrollByContent={true} />
      <Paging defaultPageSize={200} />
    </DataGrid>
  ) : null;
}
